// Records client-side runtime errors into a small sessionStorage ring so the
// admin debug page can show "errors this session". Mounted once at app root.
// Best-effort: silently no-ops on SSR or when sessionStorage is unavailable.

const CLIENT_KEY = "tigerseye:client-error-buffer";
const RING = 50;

interface ClientError {
  id: string;
  at: number;
  source: "window-error" | "unhandled-rejection";
  name: string;
  message: string;
  stack?: string;
  route?: string;
}

function shortId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

function read(): ClientError[] {
  try {
    const raw = sessionStorage.getItem(CLIENT_KEY);
    return raw ? (JSON.parse(raw) as ClientError[]) : [];
  } catch {
    return [];
  }
}

function write(items: ClientError[]) {
  try {
    sessionStorage.setItem(CLIENT_KEY, JSON.stringify(items));
  } catch {
    /* quota / disabled — ignore */
  }
}

function describe(err: unknown): { name: string; message: string; stack?: string } {
  if (err instanceof Error) {
    return { name: err.name || "Error", message: err.message || "(no message)", stack: err.stack };
  }
  return { name: "NonErrorThrown", message: typeof err === "string" ? err : String(err) };
}

function push(source: ClientError["source"], err: unknown) {
  const d = describe(err);
  const entry: ClientError = {
    id: shortId(),
    at: Date.now(),
    source,
    name: d.name,
    message: d.message,
    stack: d.stack,
    route: typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined,
  };
  const items = read();
  items.push(entry);
  if (items.length > RING) items.splice(0, items.length - RING);
  write(items);
}

let installed = false;
export function installClientErrorReporter() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (event) => push("window-error", event.error ?? event.message));
  window.addEventListener("unhandledrejection", (event) => push("unhandled-rejection", event.reason));
}
